import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { mealSuggestionService } from '@/services/mealSuggestion';
import { recipeService } from '@/services/recipe';
import { MealSuggestion, DbMealSuggestion } from '@/types/mealSuggestion';
import { Recipe } from '@/types/recipe';
import { recipeSimilarityService, SimilarityMatch } from '@/utils/recipeSimilarity';
import { useLocalStorage } from '@/hooks/useLocalStorage';

interface ProcessedSuggestion extends MealSuggestion {
  existingRecipe?: Recipe;
  similarMatches: SimilarityMatch[];
  exactMatch?: Recipe;
}

export const useMealSuggestions = (date: Date, mealType?: 'lunch' | 'dinner') => {
  const [dismissedSuggestions, setDismissedSuggestions] = useLocalStorage<string[]>('dismissed-suggestions', []);
  const dateString = format(date, 'yyyy-MM-dd');

  // Load latest meal suggestions with caching
  const { data: suggestionsData, isLoading: suggestionsLoading, refetch: refetchSuggestions } = useQuery({
    queryKey: ['meal-suggestions-latest'],
    queryFn: mealSuggestionService.getLatestSuggestions,
    staleTime: 0, // Changed from 30 minutes to 0 for immediate updates
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnMount: 'always', // Force refetch on component mount for mobile
  });

  // Load family recipes for matching
  const { data: familyRecipes = [], isLoading: recipesLoading } = useQuery({
    queryKey: ['recipes'],
    queryFn: recipeService.getRecipes,
    staleTime: 30 * 60 * 1000,
  });

  // Get suggestions for this specific date and meal type
  const getSuggestionsForDate = useMemo(() => {
    if (!suggestionsData?.success || !suggestionsData.data?.suggestions) {
      return { lunch: [], dinner: [] };
    }

    const dateSuggestions = suggestionsData.data.suggestions.suggestions.filter(
      (suggestion: MealSuggestion) => suggestion.targetDate === dateString
    );

    return {
      lunch: dateSuggestions.filter((s: MealSuggestion) => s.mealType === 'lunch'),
      dinner: dateSuggestions.filter((s: MealSuggestion) => s.mealType === 'dinner')
    };
  }, [suggestionsData, dateString]);

  // Process suggestions with matching logic (same as MealSuggestions page)
  const processedSuggestion = useMemo((): ProcessedSuggestion | null => {
    const suggestions = mealType ? getSuggestionsForDate[mealType] : [...getSuggestionsForDate.lunch, ...getSuggestionsForDate.dinner];
    if (!suggestions.length || recipesLoading) return null;

    const suggestion = suggestions[0]; // Take first suggestion for this date and meal type
    
    // Skip if dismissed
    if (dismissedSuggestions.includes(suggestion.id)) {
      return null;
    }

    let existingRecipe: Recipe | undefined;
    let exactMatch: Recipe | undefined;

    // Find exact match
    if (suggestion.isFromCollection && suggestion.recipeId) {
      existingRecipe = familyRecipes.find(recipe => recipe.id === suggestion.recipeId);
    } else {
      exactMatch = familyRecipes.find(recipe => 
        recipe.title.toLowerCase().trim() === suggestion.recipeName.toLowerCase().trim()
      );
      if (exactMatch) {
        existingRecipe = exactMatch;
      }
    }

    // Find similar matches for new suggestions
    const similarMatches = !suggestion.isFromCollection && !existingRecipe
      ? recipeSimilarityService.findSimilarRecipes(suggestion.recipeName, familyRecipes, 3)
      : [];

    return {
      ...suggestion,
      existingRecipe,
      similarMatches,
      exactMatch
    };
  }, [getSuggestionsForDate, mealType, familyRecipes, recipesLoading, dismissedSuggestions]);

  const dismissSuggestion = (suggestionId: string) => {
    setDismissedSuggestions([...dismissedSuggestions, suggestionId]);
  };

  return {
    suggestion: processedSuggestion,
    isLoading: suggestionsLoading || recipesLoading,
    dismissSuggestion,
    refetchSuggestions
  };
};